
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { IRenderProps, isRenderProp, renderChildrenWithProps } from './RenderProps';

const resolvedPromises = new WeakMap<Promise<void>, boolean>();

const promiseSubscribers = new WeakMap<Promise<void>, (() => void)[]>();

export interface IDeferredRenderStateProps {
  hasResolved: boolean;
}

export interface IDeferredRenderProps extends IRenderProps<IDeferredRenderStateProps> {
  barrier?: Promise<void>;
  placeholder?: React.ReactNode;
}

export interface IDeferredRenderState {
  hasResolved: boolean;
}

export class DeferredRender extends React.PureComponent<IDeferredRenderProps, IDeferredRenderState> {
  private _isDisposed?: boolean;

  constructor(props: IDeferredRenderProps) {
    super(props);

    const {
      barrier
    } = this.props;

    this.state = {
      hasResolved: !barrier || !!resolvedPromises.get(barrier)
    };
  }

  public componentWillReceiveProps(nextProps: IDeferredRenderProps): void {
    const {
      barrier
    } = nextProps;

    if (barrier !== this.props.barrier) {
      this.state = {
        hasResolved: !barrier || !!resolvedPromises.get(barrier)
      };
    }
  }

  public render(): JSX.Element | null {
    const {
      placeholder,
      children
    } = this.props;

    const {
      hasResolved
    } = this.state;

    if (isRenderProp(children)) {
      return (
        <>
          { children({ hasResolved }) }
        </>
      );
    } else if (hasResolved) {
      return renderChildrenWithProps(children, { hasResolved });
    } else if (placeholder) {
      return (
        <>
          { placeholder }
        </>
      );
    }

    return null;
  }

  public componentDidMount(): void {
    const {
      barrier
    } = this.props;

    if (!this.state.hasResolved && barrier) {
      this._subscribe(barrier);
    }
  }

  public componentDidUpdate(previousProps: IDeferredRenderProps): void {
    const {
      barrier
    } = this.props;

    const {
      barrier: previousBarrier
    } = previousProps;

    if (barrier !== previousBarrier) {
      if (previousBarrier) {
        this._unsubscribe(previousBarrier);
      }

      if (!this.state.hasResolved && barrier) {
        this._subscribe(barrier);
      }
    }

  }

  public componentWillUnmount(): void {
    const {
      barrier
    } = this.props;

    if (barrier) {
      this._unsubscribe(barrier);
    }

    this._isDisposed = true;
  }

  private _subscribe(barrier: Promise<void>): void {
    if (!resolvedPromises.has(barrier)) {
      if (!promiseSubscribers.has(barrier)) {
        const subscribers: (() => void)[] = [];

        promiseSubscribers.set(barrier, subscribers);

        barrier.then(() => {
          resolvedPromises.set(barrier, true);

          ReactDOM.unstable_batchedUpdates(() => {
            for (const subscriber of subscribers) {
              subscriber();
            }
          });

          promiseSubscribers.delete(barrier);
        });
      }

      {
        // tslint:disable-next-line:no-non-null-assertion
        const subscribers = promiseSubscribers.get(barrier)!;

        subscribers.push(this._onBarrier);
      }
    }
  }

  private _unsubscribe(barrier: Promise<void>): void {
    const subscribers = promiseSubscribers.get(barrier);

    if (subscribers) {
      const index = subscribers.indexOf(this._onBarrier);

      if (index > -1) {
        subscribers.splice(index, 1);
      }
    }
  }

  private _onBarrier = () => {
    if (this._isDisposed) {
      return;
    }

    this.setState({
      hasResolved: true
    });
  }
}
